<template>
  <div class="rf-prompts rf-main-content-wrapper">
    <RfLimitWarning class="mb-4" />
    <div v-if="showEmpty" class="absolute inset-0 left-60 z-0 flex h-full">
      <div class="m-auto inline-flex flex-col items-center gap-4 pt-10 text-center">
        <span class="text-heading-1 text-beige-90 uppercase">No Prompts Found</span>
        <span class="text-heading-2 font-normal!">
          Looks like you haven't created a prompt yet.
        </span>
        <RfButtonNew
          text="+ New Prompt"
          data-cy="prompts-table--create-prompt"
          size="large"
          class="mt-2"
          :disabled="userStore.isDisabledWithMessage"
          @click="showPromoDeviceTypeDialog"
        />
      </div>
    </div>
    <template v-else>
      <div class="flex min-h-12 items-center justify-between gap-16">
        <div class="flex min-w-px flex-1 shrink-0 items-center gap-4">
          <h1 class="shrink-0 text-xl font-semibold not-italic">All prompts</h1>

          <RfTagsGroup
            :filters="filters"
            :filtersLength="filtersLength"
            class="pt-2 pb-1"
            @remove="(filterKey, key) => (removeFilter(filterKey, key), refetch())"
            @clear="((filters = {}), refetch())"
          />
        </div>
      </div>
      <div class="flex items-center justify-between gap-16">
        <RfDrillDownMetricsFilters
          v-model:filtersData="filtersData"
          class="overflow-auto *:shrink-0"
          :segments="allSegmentsStore.sortedAllSegments"
          :segmentFilterIsLoading="allSegmentsStore.getSegmentsLoading"
          :appDevices="appsStore.app?.path_filter?.dimension_filters?.device_categories"
          :appDeviceTypes="appDeviceTypeEntries"
          :appLanguages="appsStore.app?.path_filter?.dimension_filters?.languages"
          @update:filtersData="refetch()"
        />
        <div class="flex grow-0 items-center gap-4">
          <RfButtonInput
            v-model="searchInputs.inputs.value[PROMPTS_SEARCH][appsStore.app.id]"
            text="Search"
            placeholder="Search"
            :icon="RfSearchIcon"
            @update:modelValue="refetch()"
          />
          <RfButtonNew
            class="shrink-0"
            data-cy="prompts-table--create-prompt"
            size="large"
            text="+ New Prompt"
            :disabled="userStore.isDisabledWithMessage"
            @click="showPromoDeviceTypeDialog"
          />
        </div>
      </div>
      <Transition mode="out-in">
        <RfAccordionCard
          v-if="isTagsSelected"
          title="Performance for selected tags"
          class="mt-3 py-3"
          @vue:mounted="getTagsMetricsData"
        >
          <RfChartsExtended :data="tagData" :skeleton="tagLoading">
            <span class="text-body text-grey-1 my-auto inline-flex pl-7">
              *Push, Email, and Invisible metrics are excluded
            </span>
          </RfChartsExtended>
        </RfAccordionCard>
      </Transition>
      <RfPollingBanner
        v-if="promptsStore.isPollingPrompts || promptsStore.warmPending"
        class="mt-4"
        :variant="promptsStore.warmFailed ? 'error' : 'info'"
        :showRetry="promptsStore.warmFailed"
        @retry="promptsStore.retryWarm"
      />
      <RfTable
        :tableData="promptsStore.table"
        :loading="promptsStore.loading"
        :loadingMore="promptsStore.loadingMore"
        :rowSkeleton="RfTableRowSkeleton"
        class="mt-4"
        :rowSkeletonProps="{ performanceCols, engagementCategoriesEnabled }"
        variant="outlined"
        type="prompts"
        @loadMore="refetch({ next: true })"
      >
        <template #pretable>
          <span class="mb-0 inline-flex items-stretch gap-4">
            <RfTableSelect
              :selected="selected"
              :tableData="promptsStore.table"
              :timezone="timezone"
              @status="status => (statusUpdate = { status, prompt: selectedValue })"
              @delete="deleteItem = valuesSelected"
              @edit="editItem = selectedValue"
              @clone="cloneItem = selectedValue"
              @export="exportItem = valuesSelected"
              @bulkEdit="bulkEditItem = valuesSelected"
              @selectAll="promptsStore.prompts.forEach(v => updateMap('set', v))"
              @deselectAll="promptsStore.prompts.forEach(v => updateMap('delete', v))"
              @selectByPreset="selectByPreset"
            />
          </span>
          <span class="my-2 inline-flex items-center gap-4">
            <RfSwitch
              v-if="isGuidesActive"
              v-model="showGuidePrompts"
              title="Show guide prompts"
              @update:modelValue="v => (setUserSetting('promptsInheritanceFilter', v), refetch())"
            />
            <RfSwitch
              v-model="performanceCols"
              title="Show performance"
              @update:modelValue="v => setUserSetting('promptsTableShowPerformance', v)"
            />

            <RfDatePicker
              ref="datePicker"
              :min="dayjs().subtract(1, 'year')"
              :max="dayjs()"
              @input="v => ((date = v), refetch())"
            />
          </span>
        </template>
        <template #thead>
          <RfTableHeader
            v-model:filters="filters"
            :appId="appsStore.app.id"
            :segments="allSegmentsStore.segments"
            :users="usersStore.currUsersEx"
            :tags="tagsStore.tags"
            :performanceCols="performanceCols"
            :engagementCategories="engagementCategories"
            :engagementCategoriesEnabled="engagementCategoriesEnabled"
            :getAppDevices="getAppDevices"
            :sort="sort"
            @update:sort="v => ((sort = v), refetch())"
            @update:filters="refetch()"
          />
        </template>
        <template #tbody>
          <RfTableRow
            v-for="prompt in promptsStore.prompts"
            :key="prompt.id"
            v-memo="[
              prompt,
              selected.has(prompt.id),
              isRemoving(prompt.id),
              performanceCols,
              date.preset?.value,
              isTagsSelected,
              engagementCategoriesEnabled,
              userStore.isDisabledWithMessage,
            ]"
            :class="{ 'rf-row-removing': isRemoving(prompt.id) }"
            :prompt="prompt"
            :disabled="userStore.isDisabledWithMessage"
            :timePeriod="date.preset?.value"
            :timezone="timezone"
            :performanceCols="performanceCols"
            :customDevicesEnabled="appsStore.app.flags?.custom_devices"
            :engagementCategoriesEnabled="engagementCategoriesEnabled"
            :selected="selected.has(prompt.id)"
            :isTagsSelected="isTagsSelected"
            @select="updateMap('set', prompt)"
            @deselect="updateMap('delete', prompt)"
            @edit="editItem = prompt"
            @delete="deleteItem = [prompt]"
            @status="status => (statusUpdate = { status, prompt })"
            @clone="cloneItem = prompt"
            @filterUser="
              (userId, userName) => (addFilter('creator_ids', userId, userName), refetch())
            "
          />
        </template>
      </RfTable>
    </template>
    <RfCreateNewPrompt ref="newPromptDialogRef" @created="onPromptCreated" />
    <RfPromptModals
      ref="modals"
      v-model:editItem="editItem"
      v-model:deleteItem="deleteItem"
      v-model:cloneItem="cloneItem"
      v-model:exportItem="exportItem"
      v-model:statusUpdate="statusUpdate"
      :timezone="timezone"
      :currApp="appsStore.app"
      :currUser="userStore.currUser"
      :isSuperAdmin="userStore.isCurrentSuperAdmin"
      :isCompanyAdmin="userStore.isCurrentAdmin"
      :onSubmitRename="v => updatePrompt([{ ...v, id: editItem.id }])"
      :showExportAlert="!!Object.values(filterDataKeys || {}).flat().length"
      @delete="submitDelete"
      @export="submitExport"
      @clone="submitClone"
      @cloneAsVariation="submitCloneAsVariation"
      @status="submitStatusUpdate"
    />
    <RfBulkEditModal
      ref="bulkEditModal"
      :show="!!bulkEditItem"
      :selectedCount="bulkEditItem?.length || 0"
      :selectedItems="bulkEditItem || []"
      :availableTags="tagsStore.tags"
      :engagementCategories="engagementCategoriesWithRestrictions"
      :engagementCategoriesEnabled="engagementCategoriesEnabled"
      :tagsEnabled="true"
      itemType="prompt"
      @apply="submitBulkEdit"
      @close="bulkEditItem = null"
    />
  </div>
</template>

<script setup>
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfButtonInput from "@/components/inputs/RfButtonInput.vue";
import RfDatePicker from "@/components/inputs/RfDatePicker.vue";
import RfSearchIcon from "@/components/icons/RfSearchIcon.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfTableHeader from "@/blocks/RfPrompts/RfTableHeader.vue";
import RfTableRow from "@/blocks/RfPrompts/RfTableRow.vue";
import RfTableRowSkeleton from "@/blocks/RfPrompts/RfTableRowSkeleton.vue";
import RfTableSelect from "@/blocks/RfPrompts/RfTableSelect.vue";
import RfPollingBanner from "@/components/RfPollingBanner.vue";
import { getAllDevices, getAllPromptTypes } from "@/utils/getDeviceName";
import { usePromptsStore } from "@/pinia/promptsStore";
import { debounce } from "@/utils/debounce";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import dayjs from "dayjs";
import { isPlacement } from "@/utils/prompts/promptHelpers";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import MetricsUtils from "@/utils/MetricsUtils";
import StatusesConstants, {
  SimplifyStatusesMapConstants,
} from "@/utils/constants/PromptStatusesConstants";
import { useAbortable } from "@/utils/composables/useAbortable";
import { useTableFilter } from "@/utils/composables/useTableFilter";
import { useTableSelect } from "@/utils/composables/useTableSelect";
import { useTableSort } from "@/utils/composables/useTableSort";
import { useRowRemoval } from "@/utils/composables/useRowRemoval";
import { useToastsStore } from "@/pinia/toastsStore";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import RfPromptModals from "@/blocks/RfModals/RfPromptModals.vue";
import { exportMultiplePromptToCsv } from "@/utils/CsvUtils";
import { getGuidePromptLink, getPromptLink } from "@/utils/getLink";
import { useTagsStore } from "@/pinia/tagsStore";
import RfAccordionCard from "@/components/cards/RfAccordionCard.vue";
import RfChartsExtended from "@/blocks/RfCharts/RfChartsExtended.vue";
import ApiMetrics from "@/apis/ApiMetrics";
import { PROMPTS_SEARCH, useSearchInputs } from "@/utils/composables/useSearchInputs";
import { chartColors } from "@/utils/constants/ColorsConstants";
import { percentage } from "@/utils/metricsHelpers";
import { useUsersStore } from "@/pinia/usersStore";
import { getUserName } from "@/utils/stringHelpers";
import { useJobsStore } from "@/pinia/jobsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useAllSegmentsStore } from "@/pinia/allSegmentsStore";
import { useUserStore } from "@/pinia/userStore";
import { usePromptStore } from "@/pinia/promptStore";
import RfTagsGroup from "@/blocks/RfTagsGroup.vue";
import RfLimitWarning from "@/components/RfCommonCards/RfLimitWarning.vue";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import RfBulkEditModal from "@/blocks/RfModals/RfBulkEditModal.vue";
import RfDrillDownMetricsFilters from "@/blocks/RfDrillDownMetricsFilters.vue";
import { useFilterData } from "@/utils/composables/useFilterData";
import RfCreateNewPrompt from "@/blocks/RfModals/RfCreateNewPrompt.vue";

defineOptions({ name: "RfPrompts" });

const constructPercentage = (numerator = [], denominator = []) => {
  const res = [];
  for (let i = 0; i < numerator?.length; i++) {
    res.push({
      key: numerator[i][0],
      value: { label: percentage(numerator[i][1], denominator[i][1]), raw: 0 },
    });
  }
  return res;
};

const debounceTime = 1000;

const router = useRouter();
const promptsStore = usePromptsStore();
const promptStore = usePromptStore();
const allSegmentsStore = useAllSegmentsStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const usersStore = useUsersStore();
const tagsStore = useTagsStore();
const toastsStore = useToastsStore();
const { getUserSetting, setUserSetting, setUserSettings } = useUserSettings();

const tagData = ref([]);
const tagLoading = ref(true);
const showEmpty = ref(false);
const date = ref({});
const editItem = ref(null);
const deleteItem = ref(null);
const cloneItem = ref(null);
const exportItem = ref(null);
const bulkEditItem = ref(null);
const statusUpdate = ref({ status: null, prompt: null });
const performanceCols = ref(true);
const showGuidePrompts = ref(false);

const { filters, addFilter, removeFilter, filtersLength, filtersConverted } = useTableFilter();
const { selected, updateMap, resetSelected, selectedValue, valuesSelected } = useTableSelect();
const sort = useTableSort(["start_date", "end_date", "default"]);
const { isRemoving, removeWithFade } = useRowRemoval();
const searchInputs = useSearchInputs();
const { engagementCategoriesEnabled, engagementCategories, engagementCategoriesWithRestrictions } =
  useEngagementCategories();
const { filtersData, filterDataKeys, getDimensionFilters } = useFilterData();

const datePicker = useTemplateRef("datePicker");
const modals = useTemplateRef("modals");
const bulkEditModalRef = useTemplateRef("bulkEditModal");
const newPromptDialogRef = useTemplateRef("newPromptDialogRef");

watch(
  [
    filtersLength,
    () => promptsStore.prompts.length,
    () => promptsStore.loading,
    () => searchInputs.inputs.value[PROMPTS_SEARCH],
  ],
  debounce(([filtersLengthIn, promptsLength, promptsLoading, searchInput]) => {
    showEmpty.value = !filtersLengthIn && !promptsLength && !promptsLoading && !searchInput;
  }, debounceTime + 100),
);

const timezone = computed(() => appsStore.app.timezone_offset);
const getAppDevices = computed(() =>
  getAllDevices(appsStore.app.custom_devices, appsStore.app.flags.custom_devices),
);
const appDeviceTypeEntries = computed(() =>
  appsStore.app.flags?.device_type_analytics
    ? getAppDevices.value.map(d => ({ key: d.value, value: d.text }))
    : [],
);
const isGuidesActive = computed(() => appsStore.app?.feature_set?.includes("sequences"));
const isTagsSelected = computed(() => !!Object.keys(filters.value?.tags || {})?.length);

const getTagsMetricsData = async () => {
  if (!date.value.preset) return;
  tagLoading.value = true;
  tagData.value = [];
  const tag_ids = filtersConverted.value.tags?.array;
  const promptIds = promptsStore.prompts.map(el => el.id);

  const data = await ApiMetrics.getTagsMetrics(appsStore.app.id, {
    path_ids: promptIds,
    metric_periods: [
      {
        period: date.value.preset.value,
        ...(date.value.preset.value === "custom" && {
          date_range: [
            date.value.startDate.format("YYYY-MM-DD"),
            date.value.endDate.format("YYYY-MM-DD"),
          ],
        }),
      },
    ],
    ...(tag_ids?.length && { tag_ids }),
    ...getDimensionFilters(),
  });

  const dataObj = data?.["raw-mixed-tags"]?.[date.value.preset.value]?.agg_data;
  if (!dataObj) return;

  const colors = [...chartColors];
  const chartData = [];

  const goalData = {};
  const uimpressionData = {};
  const impressionData = {};
  const customGoalData = {};

  for (const property in dataObj) {
    uimpressionData[property] = dataObj[property]?.uimpression || 0;
    impressionData[property] = dataObj[property]?.impression || 0;
    goalData[property] = dataObj[property]?.goal || 0;
    customGoalData[property] = dataObj[property]?.custom_goals_seen || 0;
  }

  const totalGoals = Object.values(goalData).reduce((a, b) => a + b, 0);
  const totalImpressions = Object.values(impressionData).reduce((a, b) => a + b, 0);

  chartData.push({
    label: "Users",
    data: uimpressionData,
    backgroundColor: colors.splice(Math.floor(Math.random() * colors.length), 1)[0],
    order: 1,
    totals: {
      label: "Total users",
      value: Object.values(uimpressionData).reduce((a, b) => a + b, 0),
    },
  });
  chartData.push({
    label: "Impressions",
    data: impressionData,
    backgroundColor: colors.splice(Math.floor(Math.random() * colors.length), 1)[0],
    order: 1,
    totals: { label: "Total impressions", value: totalImpressions },
  });

  const color = colors.splice(Math.floor(Math.random() * colors.length), 1)[0];
  const totalCTR = percentage(totalGoals, totalImpressions, "new");
  chartData.push({
    label: "CTR",
    data: constructPercentage(Object.entries(goalData), Object.entries(impressionData)),
    backgroundColor: color,
    borderColor: color,
    type: "line",
    order: 0,
    parsing: { xAxisKey: "key", yAxisKey: "value.raw" },
    props: { type: "percentage" },
    totals: { text: totalCTR.label, value: totalCTR.value },
  });

  chartData.push({
    label: "Clicks",
    data: goalData,
    backgroundColor: colors.splice(Math.floor(Math.random() * colors.length), 1)[0],
    order: 1,
    totals: { label: "Total clicks", value: totalGoals },
  });

  chartData.push({
    label: "Custom goal",
    data: customGoalData,
    backgroundColor: colors.splice(Math.floor(Math.random() * colors.length), 1)[0],
    order: 1,
    totals: { value: Object.values(customGoalData).reduce((a, b) => a + b, 0) },
  });
  tagData.value = chartData;
  tagLoading.value = false;
};

const getPrompts = async ({ next = false, tags = false } = {}) => {
  if (!date.value.preset) return;
  if (tags || (!next && isTagsSelected.value)) {
    tagLoading.value = true;
    tagData.value = [];
  }
  const statuses = filtersConverted.value.statuses?.string;
  const device_types = filtersConverted.value.devices?.string;
  const path_types = filtersConverted.value.prompts?.string;
  const segments_ids = filtersConverted.value.segments?.string;
  const tag_ids = filtersConverted.value.tags?.string;
  const creator_ids = filtersConverted.value.creator_ids?.string;
  const engagement_category = filtersConverted.value.engagement_category?.string;
  searchInputs.write(
    PROMPTS_SEARCH,
    appsStore.app.id,
    searchInputs.inputs.value[PROMPTS_SEARCH][appsStore.app.id],
  );

  const result = await abortableFetch(signal =>
    promptsStore.getPromptsPaged(
      appsStore.app.id,
      {
        metric_periods: [
          {
            period: date.value.preset.value,
            ...(date.value.preset.value === "custom" && {
              date_range: [
                date.value.startDate.format("YYYY-MM-DD"),
                date.value.endDate.format("YYYY-MM-DD"),
              ],
            }),
          },
        ],
        include_sequence_items: showGuidePrompts.value,
        ...(searchInputs.inputs.value[PROMPTS_SEARCH]?.[appsStore.app.id] && {
          name_search: searchInputs.inputs.value[PROMPTS_SEARCH]?.[appsStore.app.id],
        }),
        ...(statuses && { statuses }),
        ...(device_types && { device_types }),
        ...(path_types && { path_types }),
        ...(segments_ids && { segments_ids }),
        ...(tag_ids && { tag_ids }),
        ...(creator_ids && { creator_ids }),
        ...(engagement_category && { engagement_category }),
        ...(sort.value.string && { order: sort.value.string }),
        ...getDimensionFilters(),
      },
      next,
      { signal },
    ),
  );

  // Bail when aborted or upstream error — don't fire the dependent tags-metrics
  // fetch with stale/empty `promptsStore.prompts` and don't persist settings
  // from a failed request. On a real error (not an abort) clear the
  // skeleton so the tags chart doesn't spin forever; on an abort leave
  // tagLoading alone — the newer in-flight call owns it.
  if (result === null) {
    if ((tags || isTagsSelected.value) && promptsStore.error) tagLoading.value = false;
    return;
  }

  if (tags || isTagsSelected.value) getTagsMetricsData();

  await setUserSettings({
    promptsSort: sort.value.string,
    promptsStatusFilter: filtersConverted.value.statuses?.array,
    promptsSegmentFilter: filtersConverted.value.segments?.array,
    promptsTagsFilter: filtersConverted.value.tags?.array,
    promptsCreatorsFilter: filtersConverted.value.creator_ids?.array,
    promptsDeviceFilter: filtersConverted.value.devices?.array,
    promptsPathTypeFilter: filtersConverted.value.prompts?.array,
    promptsInheritanceFilter: filtersConverted.value.inheritance?.array,
    promptsEngagementCategoryFilter: filtersConverted.value.engagement_category?.array,
    guidesType: filtersConverted.value.sequence_type?.array,
    ...(date.value.preset.value !== "custom" ? { promptsDateRange: date.value.preset.value } : {}),
  });
};

const abortableFetch = useAbortable();
const refetch = debounce(async ({ next = false, tags = false } = {}) => {
  if (!next) {
    resetSelected();
    promptsStore.resetPage();
  }
  await getPrompts({ next, tags });
}, debounceTime);

const showPromoDeviceTypeDialog = () => newPromptDialogRef.value?.open();

const onPromptCreated = () => promptsStore.reloadAllLoaded(() => getPrompts());

const editPrompt = paths => {
  resetSelected();
  return promptsStore.bulkUpdate(appsStore.app.id, paths);
};

const updatePrompt = paths => {
  editPrompt(paths)
    .then(() => toastsStore.create({ type: "success", body: "Prompt updated" }))
    .catch(() => null);
};

const submitStatusUpdate = prompts => {
  editPrompt(prompts)
    .then(() => {
      modals.value?.closeStatusModal();
      toastsStore.create({ type: "success", body: "Status updated" });
    })
    .catch(() => null);
};

const submitDelete = async () => {
  const items = deleteItem.value;
  const body = `Prompt${items.length > 1 ? "s" : ""} deleted`;
  modals.value?.closeDeleteModal();

  try {
    await removeWithFade(
      items.map(p => p.id),
      async () => {
        if (items.length > 1) {
          await promptsStore.bulkDelete(appsStore.app.id, items);
        } else if (isPlacement(items[0].path_type)) {
          await promptStore.deletePath({
            appId: appsStore.app.id,
            pathId: items[0].id,
            path: items[0],
          });
        } else {
          await promptStore.deletePathGroup({
            appId: appsStore.app.id,
            pathGroupId: items[0].path_group_id,
          });
        }
        resetSelected();
        await promptsStore.reloadAllLoaded(() => getPrompts());
      },
    );
    toastsStore.create({ type: "success", body });
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  }
};

const submitExport = type => {
  if (type === "summary") {
    exportMultiplePromptToCsv(
      [...exportItem.value.values()],
      date.value.preset?.value,
      filterDataKeys,
    );
  } else {
    const metricsPeriod = { period: date.value.preset?.value };

    if (date.value.preset.value === "custom") {
      metricsPeriod.date_range = [
        date.value.startDate.format("YYYY-MM-DD"),
        date.value.endDate.format("YYYY-MM-DD"),
      ];
    }

    useJobsStore().createJob({
      appId: appsStore.app.id,
      metrics_period: metricsPeriod,
      path_ids: exportItem.value.map(i => i.id),
      export_type: type,
    });
  }
  modals.value?.closeExportModal();
};

const submitClone = async ({
  targetAppId,
  targetAppName,
  pathGroupId,
  sequenceId,
  sequenceName,
  defaultCompany,
  engagementCategory = null,
}) => {
  let body = `Cloned ${cloneItem.value.name}`;
  let timeout;

  try {
    const path = await promptStore.clonePath({
      appId: appsStore.app.id,
      pathId: cloneItem.value.id,
      targetAppId,
      pathGroupId,
      sequenceId,
      defaultCompany,
      engagementCategory,
    });

    if (appsStore.app.id === targetAppId) {
      if (path?.id) {
        const link = sequenceId
          ? getGuidePromptLink(path.id, sequenceId, appsStore.app.id)
          : getPromptLink(path.id, appsStore.app.id);
        router.push(link);
      }
      if (sequenceId && sequenceName) {
        body += ` to "${sequenceName}"`;
        timeout = 5000;
      }
    } else {
      body += ` to ${targetAppName}`;
      timeout = 5000;
    }
    toastsStore.create({ type: "success", body, timeout });
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
  modals.value?.closeCloneModal();
  resetSelected();
};

const submitCloneAsVariation = async ({ targetPathId }) => {
  try {
    await promptStore.cloneAsVariation({
      appId: appsStore.app.id,
      pathId: cloneItem.value.id,
      targetPathId,
    });
    toastsStore.create({
      type: "success",
      body: `Added "${cloneItem.value.name}" as a new variation`,
    });
    modals.value?.closeCloneModal();
    resetSelected();
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
};

const submitBulkEdit = async ({
  tagsToAdd = [],
  tagsToRemove = [],
  engagement_category,
  engagement_subcategory,
}) => {
  const bulkEditModal = bulkEditModalRef.value;
  const pathIds = bulkEditItem.value.map(p => p.id);

  try {
    if (tagsToAdd.length > 0 || tagsToRemove.length > 0) {
      await promptsStore.bulkTagUpdate(appsStore.app.id, pathIds, tagsToAdd, tagsToRemove);
    }

    if (engagement_category !== undefined) {
      const categoryUpdates = pathIds.map(id => ({
        id,
        engagement_category,
        ...(engagement_subcategory !== undefined && { engagement_subcategory }),
      }));
      await promptsStore.bulkUpdate(appsStore.app.id, categoryUpdates);
    }

    bulkEditModal?.reset();
    bulkEditItem.value = null;
    resetSelected();
    await refetch();

    toastsStore.create({
      type: "success",
      body: `${pathIds.length} prompt${pathIds.length > 1 ? "s" : ""} edited`,
    });
  } catch (error) {
    if (bulkEditModal) bulkEditModal.isProcessing.value = false;

    toastsStore.create({
      type: "error",
      body:
        error.status === 403
          ? "You don't have permission to edit prompts"
          : error.message || "Failed to edit prompts",
    });
  }
};

const selectByPreset = preset => {
  resetSelected();
  promptsStore.prompts.forEach(prompt => {
    SimplifyStatusesMapConstants[getPromptStatus(prompt, timezone.value).type].type === preset &&
      updateMap("set", prompt);
  });
};

onMounted(async () => {
  sort.value = getUserSetting("promptsSort");
  performanceCols.value = getUserSetting("promptsTableShowPerformance") ?? true;
  showGuidePrompts.value = getUserSetting("promptsInheritanceFilter") ?? false;

  await Promise.all([
    ...(allSegmentsStore.segments.length
      ? []
      : [allSegmentsStore.getSegments({ appId: appsStore.app.id })]),
    ...(tagsStore.tags?.length ? [] : [tagsStore.getTags(appsStore.app.id)]),
    usersStore.getUsers(),
  ]);

  filters.value = {
    statuses: Object.fromEntries(
      (getUserSetting("promptsStatusFilter") || []).reduce((acc, el) => {
        const status = StatusesConstants[el];
        if (status) acc.push([status.type, status.title]);
        return acc;
      }, []),
    ),
    devices: Object.fromEntries(
      (getUserSetting("promptsDeviceFilter") || []).reduce((acc, el) => {
        const device = getAppDevices.value.find(({ value }) => value === el);
        if (device) acc.push([device.value, device.text]);
        return acc;
      }, []),
    ),
    prompts: Object.fromEntries(
      (getUserSetting("promptsPathTypeFilter") || []).reduce((acc, el) => {
        const prompt = getAllPromptTypes().find(({ key }) => key === el);
        if (prompt) acc.push([prompt.key, prompt.value]);
        return acc;
      }, []),
    ),
    segments: Object.fromEntries(
      (getUserSetting("promptsSegmentFilter") || []).reduce((acc, el) => {
        const segment = allSegmentsStore.segments.find(({ key }) => key === el);
        if (segment) acc.push([segment.key, segment.name]);
        return acc;
      }, []),
    ),
    tags: Object.fromEntries(
      (getUserSetting("promptsTagsFilter") || []).reduce((acc, el) => {
        const tag = tagsStore.tags.find(({ id }) => id === el);
        if (tag) acc.push([tag.id, tag.title]);
        return acc;
      }, []),
    ),
    creator_ids: Object.fromEntries(
      (getUserSetting("promptsCreatorsFilter") || []).reduce((acc, el) => {
        const user = usersStore.users.find(({ id }) => id === el);
        if (user) acc.push([user.id, getUserName(user)]);
        return acc;
      }, []),
    ),
    engagement_category: Object.fromEntries(
      (getUserSetting("promptsEngagementCategoryFilter") || []).reduce((acc, el) => {
        const category = engagementCategories.value.find(({ value }) => value === el);
        if (category) acc.push([category.value, category.label]);
        return acc;
      }, []),
    ),
  };
  nextTick(() => {
    const preset = MetricsUtils.All_Periods.find(
      ({ value }) => value !== "since_start_date" && value === getUserSetting("promptsDateRange"),
    )?.value;

    datePicker.value?.setTimeFromPreset(preset || "last_seven_days");
  });
});
</script>
